import moment from 'moment';
import { LevelRewardInput, LevelRewardInputType } from '../../../../../__gqltypes__';
import { formatReward } from '../../components/formatReward';
import {
  LOYALTY_CAMPAIGN_ALLOWED_REWARD_TYPES,
  LoyaltyCampaignCardKind,
  LoyaltyCampaignClaimMode,
  LoyaltyCampaignCardType,
  LoyaltyCampaignItemCategory,
  LoyaltyCampaignOfferOptionRow,
  LoyaltyCampaignSkuPriceRow,
  RewardFormInput,
  SavedRewardRow,
  VanityItemType,
  VANITY_ITEM_REWARD_TYPES,
} from './types';

export const getRewardLabel = (kind: LoyaltyCampaignCardKind): string =>
  kind === 'ITEM' ? 'Item' : 'Bundle Rewards';

export const formatLoyaltyPoints = (amount?: number | null, { signed = false }: { signed?: boolean } = {}): string => {
  const safeAmount = Number(amount ?? 0);
  const normalizedAmount = Number.isFinite(safeAmount) ? safeAmount : 0;
  const prefix = signed && normalizedAmount > 0 ? '+' : '';
  return `🎖 ${prefix}${normalizedAmount} Points`;
};

export const fromRewardFormToLevelRewardInput = (reward: RewardFormInput): LevelRewardInput => ({
  type: reward.type as LevelRewardInputType,
  amount: reward.amount,
  itemId: reward.itemId || undefined,
});

export const fromLevelRewardInputToRewardForm = (reward: LevelRewardInput): RewardFormInput => ({
  type: reward.type.toString(),
  amount: reward.amount ?? 0,
  itemId: reward.itemId ?? '',
});

export const toLevelRewardInputType = (reward: SavedRewardRow): LevelRewardInputType | null => {
  const rewardType = reward.__typename ?? '';
  const aliases: Record<string, LevelRewardInputType> = {
    CurrencyUnlimitedBoost: LevelRewardInputType.CURRENCY_BOOST,
    CurrencyCategoryRandom: LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
  };

  if (aliases[rewardType]) {
    return aliases[rewardType];
  }

  const normalized = rewardType.replace(/([A-Z])/g, '_$1').replace(/^_/, '').toUpperCase();
  const asEnum = normalized as LevelRewardInputType;
  return Object.values(LevelRewardInputType).includes(asEnum) ? asEnum : null;
};

export const getItemCategoryFromRewardType = (
  rewardType: LevelRewardInputType | null
): LoyaltyCampaignItemCategory => {
  switch (rewardType) {
    case LevelRewardInputType.CURRENCY_COIN:
      return 'COINS';
    case LevelRewardInputType.CURRENCY_DIAMOND:
      return 'DIAMONDS';
    case LevelRewardInputType.CURRENCY_BOOST:
      return 'BOOSTS';
    case LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT:
    case LevelRewardInputType.CURRENCY_META_FRAGMENT_BRONZE:
    case LevelRewardInputType.CURRENCY_META_FRAGMENT_SILVER:
    case LevelRewardInputType.CURRENCY_META_FRAGMENT_GOLD:
    case LevelRewardInputType.CURRENCY_SHARD_BRONZE:
    case LevelRewardInputType.CURRENCY_SHARD_SILVER:
    case LevelRewardInputType.CURRENCY_SHARD_GOLD:
      return 'FRAGMENTS';
    case LevelRewardInputType.STICKER:
    case LevelRewardInputType.PROFILE_FRAME:
    case LevelRewardInputType.APP_SKIN:
    case LevelRewardInputType.BADGE:
    case LevelRewardInputType.POSTER:
    case LevelRewardInputType.EMOJI:
    case LevelRewardInputType.EMOJI_PACK:
      return 'VANITY';
    default:
      return 'OTHER';
  }
};

export const getItemCategoryFromReward = (reward?: SavedRewardRow): LoyaltyCampaignItemCategory =>
  getItemCategoryFromRewardType(reward ? toLevelRewardInputType(reward) : null);

export const getItemCategoryFromRewardForm = (reward?: RewardFormInput): LoyaltyCampaignItemCategory =>
  getItemCategoryFromRewardType((reward?.type as LevelRewardInputType | undefined) ?? null);

export const getVanityItemTypeFromRewardType = (rewardType: LevelRewardInputType | null): VanityItemType | null => {
  switch (rewardType) {
    case LevelRewardInputType.APP_SKIN:
      return 'VINYL';
    case LevelRewardInputType.PROFILE_FRAME:
      return 'FRAME';
    case LevelRewardInputType.STICKER:
      return 'STICKER';
    default:
      return null;
  }
};

export const getVanityItemTypeFromReward = (reward?: SavedRewardRow): VanityItemType | null =>
  getVanityItemTypeFromRewardType(reward ? toLevelRewardInputType(reward) : null);

export const getVanityItemTypeFromRewardForm = (reward?: RewardFormInput): VanityItemType | null =>
  getVanityItemTypeFromRewardType((reward?.type as LevelRewardInputType | undefined) ?? null);

export const getAllowedRewardTypesForItemCategory = (
  category: LoyaltyCampaignItemCategory | null,
  vanityItemType?: VanityItemType | null
): LevelRewardInputType[] | undefined => {
  switch (category) {
    case 'COINS':
      return [LevelRewardInputType.CURRENCY_COIN];
    case 'DIAMONDS':
      return [LevelRewardInputType.CURRENCY_DIAMOND];
    case 'BOOSTS':
      return [LevelRewardInputType.CURRENCY_BOOST];
    case 'FRAGMENTS':
      return [
        LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
        LevelRewardInputType.CURRENCY_META_FRAGMENT_BRONZE,
        LevelRewardInputType.CURRENCY_META_FRAGMENT_SILVER,
        LevelRewardInputType.CURRENCY_META_FRAGMENT_GOLD,
        LevelRewardInputType.CURRENCY_SHARD_BRONZE,
        LevelRewardInputType.CURRENCY_SHARD_SILVER,
        LevelRewardInputType.CURRENCY_SHARD_GOLD,
      ];
    case 'VANITY':
      return vanityItemType
        ? [VANITY_ITEM_REWARD_TYPES[vanityItemType]]
        : [
            LevelRewardInputType.STICKER,
            LevelRewardInputType.PROFILE_FRAME,
            LevelRewardInputType.APP_SKIN,
            LevelRewardInputType.BADGE,
            LevelRewardInputType.POSTER,
            LevelRewardInputType.EMOJI,
            LevelRewardInputType.EMOJI_PACK,
          ];
    default:
      return LOYALTY_CAMPAIGN_ALLOWED_REWARD_TYPES;
  }
};

export const getItemCategoryBadgeStyle = (category: LoyaltyCampaignItemCategory): Record<string, string> => {
  switch (category) {
    case 'COINS':
      return { backgroundColor: '#f6c445', color: '#623b00' };
    case 'DIAMONDS':
      return { backgroundColor: '#79e3ff', color: '#004f63' };
    case 'BOOSTS':
      return { backgroundColor: '#ffb454', color: '#6c3400' };
    case 'FRAGMENTS':
      return { backgroundColor: '#d6f5d6', color: '#176534' };
    case 'VANITY':
      return { backgroundColor: '#d6b0ff', color: '#542586' };
    default:
      return { backgroundColor: '#dfe3e8', color: '#4e5968' };
  }
};

export const toRewardItemId = (reward: SavedRewardRow): string => {
  if (reward.__typename === 'CurrencyBoost') {
    return reward.boostType ?? '';
  }
  if (reward.__typename === 'CurrencyUnlimitedBoost') {
    return reward.unlimitedBoostType ?? '';
  }
  if (reward.__typename === 'CurrencyCategoryFragment') {
    return reward.category?.id ?? '';
  }
  if (reward.__typename === 'ContentPackReward') {
    return reward.contentPack?.id ?? '';
  }
  return reward.id ?? '';
};

export const toRewardFormInput = (reward: SavedRewardRow): RewardFormInput | null => {
  const type = toLevelRewardInputType(reward);
  if (!type) {
    return null;
  }
  return {
    type,
    amount: reward.amount ?? 1,
    itemId: toRewardItemId(reward),
  };
};

export const toLevelRewardInput = (reward: SavedRewardRow): LevelRewardInput | null => {
  const type = toLevelRewardInputType(reward);
  if (!type) {
    return null;
  }
  return {
    type,
    amount: reward.amount ?? 1,
    itemId: toRewardItemId(reward) || undefined,
  };
};

export const formatSavedReward = (reward: SavedRewardRow): string => {
  const levelReward = toLevelRewardInput(reward);
  if (!levelReward) {
    return reward.__typename ?? 'UNKNOWN_REWARD';
  }
  return formatReward(levelReward);
};

export const parseSkus = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .replace(/\\n/g, '\n')
        .split(/[\n,\s]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    )
  );

export const toPickerDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (moment.isMoment(value)) {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

const LOYALTY_POINTS_BY_PRICE_CENTS: Record<number, number> = {
  99: 100,
  199: 150,
  299: 200,
  399: 250,
  499: 300,
  599: 350,
  699: 400,
  799: 450,
  899: 500,
  999: 550,
  1299: 600,
  1499: 700,
  1799: 800,
  1999: 900,
  4999: 1500,
  9999: 3000,
};

export const buildSkuPriceMap = (rows: LoyaltyCampaignSkuPriceRow[]): Record<string, number> => {
  const next: Record<string, number> = {};
  for (const row of rows ?? []) {
    const sku = (row?.sku ?? '').trim();
    const priceUsd = Number(row?.priceUsd);
    if (sku && Number.isFinite(priceUsd)) {
      next[sku] = priceUsd;
    }
  }
  return next;
};

export const buildOfferPriceMap = (rows: LoyaltyCampaignOfferOptionRow[]): Record<string, number> => {
  const next: Record<string, number> = {};
  for (const row of rows ?? []) {
    const offerId = (row?.id ?? '').trim();
    const priceUsd = Number(row?.priceUsd);
    if (offerId && Number.isFinite(priceUsd)) {
      next[offerId] = priceUsd;
    }
  }
  return next;
};

export const buildOfferSkuMap = (rows: LoyaltyCampaignOfferOptionRow[]): Record<string, string[]> => {
  const next: Record<string, string[]> = {};
  for (const row of rows ?? []) {
    const offerId = (row?.id ?? '').trim();
    const skus = (row?.skus ?? []).map((sku) => sku.trim()).filter(Boolean);
    if (offerId) {
      next[offerId] = skus;
    }
  }
  return next;
};

export const getOfferPriceUSD = (offerId: string, offerPriceMap: Record<string, number>): number | null => {
  const price = offerPriceMap[offerId];
  return Number.isFinite(price) ? price : null;
};

export const findOfferIdBySku = (sku: string, offerSkuMap: Record<string, string[]>): string | null => {
  const normalizedSku = sku.trim();
  if (!normalizedSku) {
    return null;
  }
  const matchedOffer = Object.entries(offerSkuMap).find(([, skus]) => skus.includes(normalizedSku));
  return matchedOffer?.[0] ?? null;
};

export const getSkuPriceUSD = (sku: string, skuPriceMap: Record<string, number>): number | null => {
  const price = skuPriceMap[sku];
  return Number.isFinite(price) ? price : null;
};

export const formatSkuOptionLabel = (sku: string, skuPriceMap: Record<string, number>): string => {
  const price = getSkuPriceUSD(sku, skuPriceMap);
  if (price === null) {
    return sku;
  }
  return `${sku} ($${price.toFixed(2)})`;
};

export const getSkuPlatformLabel = (sku: string): string => {
  const normalizedSku = sku.trim().toLowerCase();
  if (!normalizedSku) {
    return 'SKU';
  }
  if (normalizedSku.includes('.ios.') || normalizedSku.includes('ios')) {
    return 'iOS';
  }
  if (normalizedSku.includes('.android.') || normalizedSku.includes('android')) {
    return 'Android';
  }
  return 'SKU';
};

export const formatOfferOptionLabel = (offer: LoyaltyCampaignOfferOptionRow): string => {
  const priceUsd = Number(offer.priceUsd);
  if (Number.isFinite(priceUsd)) {
    return `${offer.label} ($${priceUsd.toFixed(2)})`;
  }
  return offer.label;
};

export const getLoyaltyPointsByPrice = (priceUsd: number): number | null => {
  const cents = Math.round(priceUsd * 100);
  const points = LOYALTY_POINTS_BY_PRICE_CENTS[cents];
  return Number.isFinite(points) ? points : null;
};

export const formatCompactNumber = (value: number): string => value.toLocaleString('en-US');

export const getCardClaimType = (
  maxClaims?: number | null,
  fallback: LoyaltyCampaignCardType = 'ONE_TIME'
): LoyaltyCampaignCardType => {
  if (maxClaims === 0 || fallback === 'INFINITE') {
    return 'INFINITE';
  }
  return 'ONE_TIME';
};

export const getCardClaimMode = (maxClaims?: number | null): LoyaltyCampaignClaimMode => {
  if (maxClaims === 0) {
    return 'INFINITE';
  }
  if ((maxClaims ?? 1) > 1) {
    return 'CUSTOM';
  }
  return 'ONE_TIME';
};

export const getClaimBadgeLabel = (claimType: LoyaltyCampaignCardType): string =>
  claimType === 'INFINITE' ? 'UNLIMITED' : 'LIMITED';

export const toTitleLabel = (value: string): string =>
  value
    .replace(/\.[0-9]+$/, '')
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const scrollToSection = (sectionId: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
